import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import { getFreshProductTrace, TRACE_WINDOW_MS } from "../../../components/firebase/freshProductTrace";
import FarmProductTraceTimeline, { formatTraceDate, traceDateKey, traceBatchLabel } from "./FarmProductTraceTimeline";
import "./farmProductTrace.css";

export default function FarmProductTrace({ plant }) {
  const language = useSelector((state) => state.app.language);
  const sv = language === 0;
  const [searchParams, setSearchParams] = useSearchParams();
  const { productId, batchDate } = useParams();
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const section = useRef(null);
  const scrolled = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getFreshProductTrace({ id: plant.id, firebaseId: plant.firebaseId }).then((items) => {
      if (active) { setBatches(items); setLoading(false); }
    }).catch(() => {
      if (active) { setError(true); setLoading(false); }
    });
    return () => { active = false; };
  }, [plant.id, plant.firebaseId, attempt]);

  // Expire results even if someone leaves the page open across the cutoff.
  useEffect(() => {
    const timer = setInterval(() => setBatches((items) => items.filter((item) => item.harvestDate >= Date.now() - TRACE_WINDOW_MS)), 60000);
    return () => clearInterval(timer);
  }, []);

  // The customer's label identifies a harvest date. Keep all source batches
  // for that date together, including the components of a mix.
  const harvests = [...batches.reduce((groups, batch) => {
    const code = traceBatchLabel(batch.harvestDate);
    if (!groups.has(code)) groups.set(code, { code, harvestDate: batch.harvestDate, batches: [] });
    groups.get(code).batches.push(batch);
    return groups;
  }, new Map()).values()];
  const requestedBatch = batchDate || searchParams.get("batch");
  const requestedDate = searchParams.get("date");
  const dateKey = requestedDate && /^\d{4}-\d{2}-\d{2}$/.test(requestedDate)
    ? requestedDate : requestedDate && !/^\d{6}$/.test(requestedDate) && Number.isFinite(Number(requestedDate)) && !Number.isNaN(new Date(Number(requestedDate)).getTime())
      ? traceDateKey(Number(requestedDate)) : null;
  const match = requestedBatch
    ? harvests.find((harvest) => harvest.code === requestedBatch || harvest.batches.some((batch) => batch.firebaseId === requestedBatch || batch.batchId === requestedBatch))
    : harvests.find((harvest) => harvest.code === requestedDate || (dateKey && traceDateKey(harvest.harvestDate) === dateKey));
  const requested = !!(requestedBatch || requestedDate);
  const selected = requested ? match : harvests[0];

  const scrollRequest = requestedBatch || requestedDate;
  const matchedCode = match?.code;
  useEffect(() => {
    if (!loading && !error && matchedCode && scrollRequest && scrolled.current !== scrollRequest) {
      scrolled.current = scrollRequest;
      section.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }
  }, [loading, error, matchedCode, scrollRequest]);

  const selectBatch = (harvest) => {
    const next = new URLSearchParams(searchParams);
    next.delete("date");
    // Selecting another date should keep a dated route in sync without
    // scrolling the user away from the picker they are already using.
    scrolled.current = harvest.code;
    if (batchDate) {
      next.delete("batch");
      navigate({ pathname: `/farm/product/${encodeURIComponent(productId)}/${harvest.code}`, search: next.toString() },
        { replace: true, preventScrollReset: true });
    } else {
      next.set("batch", harvest.code);
      setSearchParams(next, { replace: true, preventScrollReset: true });
    }
  };

  return <section className="BatchTrace" id="ProductTrace" ref={section} aria-labelledby="batch-trace-title">
    <div className="BatchTraceHeader">
      <div>
        <h2 id="batch-trace-title">{sv ? "SPÅRA DIN SKÖRD" : "TRACE YOUR HARVEST"}</h2>
        <p>{sv ? "Välj det sexsiffriga batchnumret på din etikett. Här ser du när ditt mikrogrönt såddes och skördades – och av vem." : "Choose the six-digit batch number on your label to see when your microgreens were sown and harvested, and by whom."}</p>
      </div>
    </div>
    {loading ? <div className="BatchTraceMessage" role="status"><span className="BatchTracePulse" />{sv ? "Hämtar din skörd…" : "Loading your harvest…"}</div>
      : error ? <div className="BatchTraceMessage" role="alert"><p>{sv ? "Vi kunde inte hämta din skörd just nu. Försök gärna igen." : "We couldn't load your harvest right now. Please try again."}</p><button onClick={() => setAttempt((value) => value + 1)}>{sv ? "Försök igen" : "Try again"}</button></div>
      : batches.length === 0 ? <div className="BatchTraceMessage" role="status"><SpaOutlinedIcon /><h3>{sv ? "Ingen aktuell skörd att visa" : "No recent harvest to show"}</h3><p>{sv ? "Du kan spåra din skörd här i 30 dagar efter skördedatumet." : "You can trace your harvest here for 30 days after it was harvested."}</p></div>
      : <>
        <div className="BatchTracePickerIntro">
          <div className="BatchTracePickerHeading"><h3>{sv ? "Välj din batch" : "Choose your batch"}</h3></div>
          <p>{sv ? "Matcha numret med etiketten på din förpackning." : "Match the number to the label on your package."}</p>
        </div>
        <div className="BatchTraceWorkspace">
          <div className="BatchTracePicker">
            <div className="BatchTraceChoices" role="group" aria-label={sv ? "Batcher" : "Batches"}>
              {harvests.map((harvest) => <button type="button" key={harvest.code} aria-pressed={selected?.code === harvest.code} onClick={() => selectBatch(harvest)}>
                <span><strong>{harvest.code}</strong><time dateTime={traceDateKey(harvest.harvestDate)}>{formatTraceDate(harvest.harvestDate, language)}</time></span><ArrowForwardIcon aria-hidden="true" />
              </button>)}
            </div>
          </div>
          {selected ? <FarmProductTraceTimeline batches={selected.batches} language={language} /> : <div className="BatchTraceMessage" role="status"><h3>{sv ? "Batchen är inte tillgänglig" : "Batch not available"}</h3><p>{sv ? "Vi hittar inte din batch bland de senaste 30 dagarnas skördar. Kontrollera numret på din etikett och välj det i listan." : "We couldn’t find your batch among the last 30 days of harvests. Check the number on your label and select it from the list."}</p></div>}
        </div>
      </>}
  </section>;
}
